import { createApi } from "@reduxjs/toolkit/query/react";
import { privateBaseQuery } from "@/api/base/private/privateBaseQuery";

export type Locale = "kk" | "ru" | "en" | "fr";

/**
 * Backend often returns localized objects: { kk, ru, en }.
 * We keep it loose to avoid breaking if backend returns a plain string.
 */
export type LocalizedName =
  | string
  | {
      kk?: string | null;
      ru?: string | null;
      en?: string | null;
      fr?: string | null;
      [k: string]: unknown;
    };

export interface CountryEntity {
  id: number;
  name?: string | null;
  title?: string | null;
  [k: string]: unknown;
}

export interface CityEntity {
  id: number;
  name?: string | null;
  title?: string | null;
  country_id?: number | null;
  [k: string]: unknown;
}

export interface OrganizationEntity {
  id: number;
  city_id?: number | null;
  type?: string | null;
  name?: LocalizedName | null;
  slug?: string | null;
  additional?: unknown;
  created_at?: string | null;
  updated_at?: string | null;

  // optional expands
  city?: unknown;
  country?: unknown;

  [k: string]: unknown;
}

export interface PaginatedEntities<T> {
  data: T[];
  current_page?: number;
  last_page?: number;
  per_page?: number;
  total?: number;
  from?: number;
  to?: number;
  prev_page_url?: string | null;
  next_page_url?: string | null;
  [k: string]: unknown;
}

/**
 * Generic envelope seen in multiple endpoints:
 * { success/status, entities } or { success/status, entity }
 */
type EntitiesEnvelope<T> = {
  entities?: T;
  entity?: T;
  success?: boolean;
  status?: string;
  status_code?: string;
  [k: string]: unknown;
};

export interface GetCountriesParams {
  search?: string;
  limit?: number;
}

export interface GetCitiesParams {
  country_id?: number;
  region_id?: number;
  search?: string;
  limit?: number;
}

export interface GetOrganizationsParams {
  city_id?: number;
  country_id?: number;
  type?: string;
  slug?: string;
  search?: string;
  with_city?: boolean;
  with_country?: boolean;
  limit?: number;
  paginable?: boolean;
  page?: number;
}

function unwrapEntities<T>(raw: unknown): T | null {
  if (!raw || typeof raw !== "object") return null;

  const r = raw as EntitiesEnvelope<T> & { data?: unknown };

  // common: { entities: ... }
  if ("entities" in r && r.entities != null) return r.entities as T;

  // some endpoints might return { data: ... }
  if ("data" in r && (r as any).data != null) return (r as any).data as T;

  return null;
}

/**
 * Backend sometimes returns UTF-8 text that was decoded as ISO-8859-1/Windows-1251,
 * so Russian/Kazakh names look like "ÐÑÑÐ°Ð½Ð°" instead of "Астана".
 *
 * This helper tries to detect such cases (all chars <= 0xff) and re-decodes the
 * underlying bytes as UTF-8. For normal Unicode strings (with chars > 0xff),
 * it returns the original value unchanged.
 */
export function decodePossibleUtf8FromLatin1(
  value: string | null | undefined,
): string {
  if (value == null) return "";

  const str = value.toString();

  // If original already has Cyrillic letters, assume it's correct.
  if (/[\u0400-\u04FF]/.test(str)) return str;

  /**
   * Many backends mistakenly decode UTF‑8 bytes using Windows‑1252 and then
   * serialize the result to JSON. That produces strings like:
   * - "ÐÐ°Ð·Ð°Ñ…ÑÑ‚Ð°Ð½" (classic mojibake)
   * - and also sequences containing cp1252 specials, e.g. "…", "š", "‚", etc.
   *
   * To recover the original UTF‑8 bytes we must reverse the Windows‑1252
   * special mapping for bytes 0x80..0x9F.
   */
  const cp1252UnicodeToByte: Record<number, number> = {
    0x20ac: 0x80, // €
    0x201a: 0x82, // ‚
    0x0192: 0x83, // ƒ
    0x201e: 0x84, // „
    0x2026: 0x85, // …
    0x2020: 0x86, // †
    0x2021: 0x87, // ‡
    0x02c6: 0x88, // ˆ
    0x2030: 0x89, // ‰
    0x0160: 0x8a, // Š
    0x2039: 0x8b, // ‹
    0x0152: 0x8c, // Œ
    0x017d: 0x8e, // Ž
    0x2018: 0x91, // ‘
    0x2019: 0x92, // ’
    0x201c: 0x93, // “
    0x201d: 0x94, // ”
    0x2022: 0x95, // •
    0x2013: 0x96, // –
    0x2014: 0x97, // —
    0x02dc: 0x98, // ˜
    0x2122: 0x99, // ™
    0x0161: 0x9a, // š
    0x203a: 0x9b, // ›
    0x0153: 0x9c, // œ
    0x017e: 0x9e, // ž
    0x0178: 0x9f, // Ÿ
  };

  try {
    const bytes = new Uint8Array(str.length);
    let hadUnknown = false;

    for (let i = 0; i < str.length; i += 1) {
      const code = str.charCodeAt(i);

      if (code <= 0xff) {
        // U+0000..U+00FF map 1:1 to byte values (incl. control chars U+0080..U+009F)
        bytes[i] = code;
        continue;
      }

      const mapped = cp1252UnicodeToByte[code];
      if (mapped != null) {
        bytes[i] = mapped;
        continue;
      }

      // Unknown char (> 0xff and not a cp1252 special). We can't safely map it.
      // Mark and substitute with '?' to keep indices stable.
      hadUnknown = true;
      bytes[i] = 0x3f;
    }

    // eslint-disable-next-line no-undef
    if (typeof TextDecoder === "undefined") return str;

    const decoded = new TextDecoder("utf-8").decode(bytes);

    const decodedHasCyrillic = /[\u0400-\u04FF]/.test(decoded);
    const hasReplacement = decoded.includes("\uFFFD");
    const hasControl = /[\u0000-\u001F\u007F]/.test(decoded);

    // Accept only if it clearly improved into readable Cyrillic
    // and we did not introduce replacement/control characters.
    if (decodedHasCyrillic && !hasReplacement && !hasControl && !hadUnknown) {
      return decoded;
    }

    // If we had unknowns, we still might get a valid decode for many real cases,
    // but keep it conservative: only accept if it improved and didn't produce �.
    if (decodedHasCyrillic && !hasReplacement && !hasControl) {
      return decoded;
    }

    return str;
  } catch {
    return str;
  }
}

export const organizationsApi = createApi({
  reducerPath: "organizationsApi",
  baseQuery: privateBaseQuery(),
  tagTypes: ["Countries", "Cities", "Organizations"],
  endpoints: (builder) => ({
    getCountries: builder.query<CountryEntity[], GetCountriesParams | void>({
      query: (params) => ({
        url: "/countries",
        method: "GET",
        params,
      }),
      transformResponse: (response: unknown) => {
        const entities = unwrapEntities<CountryEntity[] | PaginatedEntities<CountryEntity>>(
          response,
        );

        let list: CountryEntity[] = [];

        if (Array.isArray(entities)) {
          list = entities;
        } else if (
          entities &&
          typeof entities === "object" &&
          Array.isArray((entities as any).data)
        ) {
          // support paginated wrapper if backend returns it
          list = (entities as any).data as CountryEntity[];
        }

        // Normalize mis-encoded names/titles at the edge so the rest of the app
        // always deals with decoded strings.
        return list.map((c) => ({
          ...c,
          name: decodePossibleUtf8FromLatin1(c.name as string | null | undefined),
          title: decodePossibleUtf8FromLatin1((c as any).title as string | null | undefined),
        }));
      },
      providesTags: [{ type: "Countries" as const, id: "LIST" }],
    }),

    getCities: builder.query<CityEntity[], GetCitiesParams | void>({
      query: (params) => ({
        url: "/cities",
        method: "GET",
        params,
      }),
      transformResponse: (response: unknown) => {
        const entities = unwrapEntities<CityEntity[] | PaginatedEntities<CityEntity>>(response);

        let list: CityEntity[] = [];

        if (Array.isArray(entities)) {
          list = entities;
        } else if (
          entities &&
          typeof entities === "object" &&
          Array.isArray((entities as any).data)
        ) {
          list = (entities as any).data as CityEntity[];
        }

        return list.map((c) => ({
          ...c,
          name: decodePossibleUtf8FromLatin1(c.name as string | null | undefined),
          title: decodePossibleUtf8FromLatin1((c as any).title as string | null | undefined),
        }));
      },
      providesTags: [{ type: "Cities" as const, id: "LIST" }],
    }),

    getOrganizations: builder.query<PaginatedEntities<OrganizationEntity>, GetOrganizationsParams>({
      query: (params) => ({
        url: "/organizations",
        method: "GET",
        params,
      }),
      transformResponse: (response: unknown) => {
        const entities = unwrapEntities<PaginatedEntities<OrganizationEntity>>(response);

        if (
          entities &&
          typeof entities === "object" &&
          Array.isArray((entities as any).data)
        ) {
          return entities;
        }

        // fallback to empty pagination shape
        return {
          data: [],
          current_page: 1,
          last_page: 1,
          per_page: 0,
          total: 0,
          from: 0,
          to: 0,
          prev_page_url: null,
          next_page_url: null,
        };
      },
      providesTags: [{ type: "Organizations" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useGetCountriesQuery,
  useGetCitiesQuery,
  useGetOrganizationsQuery,
} = organizationsApi;

export function pickLocalizedName(
  value: LocalizedName | null | undefined,
  locale: Locale = "ru",
): string {
  if (!value) return "";
  if (typeof value === "string") {
    return decodePossibleUtf8FromLatin1(value);
  }

  const byLocale =
    (value[locale] as string | null | undefined) ??
    (value.ru as string | null | undefined) ??
    (value.kk as string | null | undefined) ??
    (value.en as string | null | undefined);

  const raw = (byLocale ?? "").toString();
  return decodePossibleUtf8FromLatin1(raw);
}
